import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

import {
  MLA_LOAN_OFFICER_FIELDS,
  MlaLoanDetails,
  MlaLoanDetailsResponse,
  MlaLoanOfficer,
  MlaLoanOfficerResponse,
  MlaLoanUpdateRequest,
} from '@ld.consumer-portal/models';
import { MlaAuthService } from './mla-auth.service';
import { LoggingService } from '@ld.consumer-portal/utils';

@Injectable({
  providedIn: 'root',
})
export class MlaLoanService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly authService = inject(MlaAuthService);
  private readonly loggingService = inject(LoggingService);

  private loanOfficerCache = new Map<string, MlaLoanOfficer>();

  private get mlaApiUrl(): string {
    return this.env.appSettings.mlaApiUrl || '';
  }

  async getLoanDetails(loanId: string): Promise<MlaLoanDetails | null> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaLoanDetailsResponse>(url));
      return response?.success ? response.data : null;
    } catch (error) {
      this.loggingService.logException(error, 'MlaLoanService: Error fetching loan details');
      return null;
    }
  }

  async updateLoan(loanId: string, updates: MlaLoanUpdateRequest): Promise<boolean> {
    const url = `${this.mlaApiUrl}api/v1/loan/${loanId}`;

    try {
      const response = await firstValueFrom(this.http.patch<{ success: boolean }>(url, updates));

      return response?.success || false;
    } catch (error) {
      this.loggingService.logException(error, 'MlaLoanService: Error updating loan');
      return false;
    }
  }

  async getLoanOfficer(loanOfficerId: string): Promise<MlaLoanOfficer | null> {
    const cached = this.loanOfficerCache.get(loanOfficerId);
    if (cached) {
      return cached;
    }

    const fields = MLA_LOAN_OFFICER_FIELDS.join(',');
    const url = `${this.mlaApiUrl}api/v1/loan-officer/${loanOfficerId}?fields=${fields}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaLoanOfficerResponse>(url));

      if (response?.success && response.data) {
        this.loanOfficerCache.set(loanOfficerId, response.data);
        return response.data;
      }
      return null;
    } catch (error) {
      this.loggingService.logException(error, 'MlaLoanService: Error fetching loan officer');
      return null;
    }
  }

  async getLoanOfficerByName(name: string): Promise<MlaLoanOfficer | null> {
    const url = `${this.mlaApiUrl}api/v1/loan-officer/byname/${encodeURIComponent(name)}`;

    try {
      const response = await firstValueFrom(this.http.get<MlaLoanOfficerResponse>(url));
      return response?.success ? response.data : null;
    } catch (error) {
      this.loggingService.logException(error, 'MlaLoanService: Error fetching loan officer by name');
      return null;
    }
  }

  async getLoanOfficerForLoan(loanId: string): Promise<MlaLoanOfficer | null> {
    try {
      const loan = await this.getLoanDetails(loanId);
      if (!loan?.loanOfficerId) {
        return null;
      }
      return this.getLoanOfficer(loan.loanOfficerId);
    } catch {
      return null;
    }
  }

  clearLoanOfficerCache(): void {
    this.loanOfficerCache.clear();
    sessionStorage.removeItem('loanOfficer');
  }

  setLoanChannelFromOfficer(loanOfficer: MlaLoanOfficer | null): void {
    const channel = loanOfficer?.channel === 'Direct' ? 'Direct' : 'Retail';
    this.authService.setLoanChannel(channel);
  }
}
